/**
 * Normalisation et matching partagés pour toutes les barres de recherche.
 *
 * Règles (identiques web / mobile / index de recherche Cloud) :
 *  - insensible à la casse
 *  - insensible aux accents ("boite" trouve "Boîte")   → GitLab #1075
 *  - recherche en SOUS-CHAÎNE, pas en préfixe          → GitLab #1076
 *    ("trous" trouve "Boîte à trous")
 *  - multi-mots : chaque mot saisi doit être présent dans au moins un des
 *    champs recherchés ("boite trous" trouve "Boîte à trous", dans n'importe
 *    quel ordre)
 *  - la ponctuation saisie (trait d'union, guillemets, n°…) sépare les mots
 *    comme une espace : "zoe ete" et "zoe n°2" trouvent "Ind Zoé-Été « n°2 »"
 *    (recette 12.1.0, COL-34 / V12.1.0-11). Un nombre décimal ou un numéro
 *    de version ("12.1.0", "1,5") reste un seul mot.
 *  - un petit nombre (1 ou 2 chiffres) désigne un numéro : il doit être un
 *    mot entier du champ, pas un morceau ("ind 4" trouve "Ind 4" mais pas
 *    "Ind 14", ni une puce qui contient un 4). Les nombres plus longs (puce,
 *    GAN) restent cherchés en sous-chaîne.
 *
 * La normalisation doit rester identique à celle appliquée par la Cloud
 * Function `syncSearchIndex` (champs `_search*` de la base Enterprise
 * "search"), sinon les deux chemins de recherche divergent.
 */

/**
 * Normalise une chaîne : sans accents, minuscules, trimmée.
 * @param {*} str
 * @returns {string}
 */
export const normalizeSearchTerm = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
};

/**
 * Découpe un terme de recherche en mots normalisés. Tout ce qui n'est ni une
 * lettre ni un chiffre (espaces, traits d'union, guillemets, ponctuation,
 * symboles) sert de séparateur.
 * @param {string} term
 * @returns {string[]} mots normalisés, sans vides
 */
const TOKEN_PATTERN = /\d+(?:[.,]\d+)+|[\p{L}\p{N}]+/gu;
const tokenize = (normalized) => normalized.match(TOKEN_PATTERN) || [];

export const splitSearchWords = (term) => tokenize(normalizeSearchTerm(term));

const isShortNumber = (word) => /^\d{1,2}$/.test(word);

// Un petit nombre correspond à un mot entier, ou à la partie entière d'un
// nombre décimal / numéro de version ("12" trouve "12.1.0").
const matchesNumberToken = (word, tokens) => tokens.some((token) =>
  token === word || token.startsWith(`${word}.`) || token.startsWith(`${word},`));

/**
 * Vrai si chaque mot recherché apparaît dans au moins un des champs fournis.
 * @param {Array<*>} fields - champs à fouiller (les non-chaînes sont ignorés)
 * @param {string[]} words - mots déjà normalisés (cf. splitSearchWords)
 * @returns {boolean}
 */
export const matchesSearchWords = (fields, words) => {
  if (!words || words.length === 0) return true;

  const haystack = (fields || [])
    .map(normalizeSearchTerm)
    .filter(Boolean)
    .map((text) => ({ text, tokens: tokenize(text) }));

  if (haystack.length === 0) return false;

  return words.every((word) => haystack.some(({ text, tokens }) => (
    isShortNumber(word) ? matchesNumberToken(word, tokens) : text.includes(word)
  )));
};

/**
 * Raccourci : normalise le terme et teste les champs en une fois.
 * @param {Array<*>} fields
 * @param {string} term - terme brut saisi par l'utilisateur
 * @returns {boolean}
 */
export const matchesSearchTerm = (fields, term) =>
  matchesSearchWords(fields, splitSearchWords(term));
